/**
 * One AudioWorklet module, added to a context once.
 *
 * Every channel strip asks for its loudness tap when the project is first
 * applied, and they all ask in the same tick. A flag set *after* the module
 * had loaded let every one of them call `addModule` before the first had
 * finished, so the processor was registered once and then thrown at N more
 * times — an uncaught error in the worklet's console for every track but one.
 * Nothing broke, because the taps that lost the race still found the processor
 * registered by the one that won, which is why it went unnoticed until the app
 * was run with the console open.
 *
 * Sharing the in-flight promise is the whole fix: the second caller waits on
 * the first's load rather than starting its own. A load that fails is
 * forgotten, so the next caller tries again rather than inheriting the failure
 * for the life of the context. Found later, when an effect moved into a
 * worklet: the promise was shared per *module*, so a second context — every
 * offline render makes one — was told the processor was registered when it
 * was not. It is shared per context now.
 */
export class WorkletModule {
  /**
   * One load per context, not one per module: a processor registered on the
   * live context is unknown to the offline one a render builds, and an
   * effect that lives in a worklet is created on both. Keyed weakly, so a
   * finished render's context is not held by the module that served it.
   */
  private readonly loading = new WeakMap<BaseAudioContext, Promise<void>>();

  constructor(private readonly source: string) {}

  ensure(ctx: BaseAudioContext): Promise<void> {
    const pending = this.loading.get(ctx);
    if (pending) return pending;
    const url = URL.createObjectURL(new Blob([this.source], { type: 'text/javascript' }));
    const load = ctx.audioWorklet.addModule(url).finally(() => URL.revokeObjectURL(url));
    this.loading.set(ctx, load);
    load.catch(() => {
      if (this.loading.get(ctx) === load) this.loading.delete(ctx);
    });
    return load;
  }
}
